import { z } from "zod";

import { wrapSuccessResponse } from "./";

/**
 * Zod schema for lead creation request
 */
export const leadCreateRequestSchema = z
  .object({
    name: z.string().min(1, "Name is required"),
    email: z.string().email("Invalid email format"),
    mobileNumber: z
      .string()
      .regex(
        /^(5){1}(0|2|4|5|6|8){1}[0-9]{7}$/,
        "Mobile number must start with 5 followed by 0,2,4,5,6,8 and 7 more digits",
      ),
    income: z
      .number()
      .min(5000, "Salary must be at least 5000")
      .max(999_999, "Salary must be at most 999999"),
    hasUaeIncomeAccount: z
      .number()
      .refine((val) => val === 0 || val === 1, {
        message: "hasUaeIncomeAccount must be 0 or 1",
      })
      .optional(),
    city: z.number().positive("City ID must be a positive number"),
    homeCountry: z
      .number()
      .positive("Home country ID must be a positive number"),
    isWhatsappEnabled: z.literal(0).or(z.literal(1)).optional(),
    countryProductId: z
      .number()
      .positive("Country product ID must be a positive number")
      .optional(),
    assignedTo: z.string().trim().optional(),
  })
  .strict();

const DDFieldNumber = z.object({
  dataType: z.number().int(),
  value: z.number(),
  id: z.number().int().optional(),
});

const DDFieldString = z.object({
  dataType: z.number().int(),
  value: z.string(),
  id: z.number().int().optional(),
});

export const leadUpdateRequestSchema = z
  .object({
    employerNameDD: z
      .object({
        dataType: z.number().int(),
        value: z
          .string()
          .min(2, "Employer name must be at least 2 characters")
          .max(100, "Employer name must be at most 100 characters")
          .regex(
            /^[a-zA-Z0-9\s.,&-]+$/,
            "Employer name can only contain letters, numbers, spaces, and basic punctuation (., &, -)",
          ),
      })
      .optional(),
    employmentTypeDD: DDFieldNumber.optional(),
    creditHistoryAgeDD: z
      .object({
        dataType: z.number().int(),
        value: z
          .number()
          .int("Credit history age must be a whole number")
          .min(0, "Credit history age must be at least 0 months")
          .max(480, "Credit history age must be at most 480 months (40 years)"),
      })
      .optional(),
    creditScoreRangeDD: z
      .object({
        dataType: z.number().int(),
        value: z
          .number()
          .int("Credit score must be a whole number")
          .min(300, "Credit score must be at least 300")
          .max(900, "Credit score must be at most 900"),
      })
      .optional(),
    creditCardDesignationDD: DDFieldNumber.optional(),
    websiteDD: z
      .object({
        dataType: z.number().int(),
        value: z
          .string()
          .url("Must be a valid URL (https:// or http://)")
          .regex(
            /^https?:\/\/[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
            "URL must contain a valid domain (.com, .ae, etc.)",
          )
          .optional()
          .or(z.literal("")),
      })
      .optional(),
    landLineDD: z
      .object({
        dataType: z.number().int(),
        value: z
          .string()
          .regex(
            /^\+?[0-9-]{6,15}$/,
            "Landline must be 6-15 digits with optional + and hyphens (e.g., +971-4-XXXXXXX)",
          )
          .refine((val) => {
            // Remove + and - to count only digits
            const digitsOnly = val.replace(/[+-]/g, "");
            return digitsOnly.length >= 6 && digitsOnly.length <= 15;
          }, "Landline must contain 6-15 digits total")
          .optional()
          .or(z.literal("")),
      })
      .optional(),
    existingCreditDurationDD: z
      .object({
        dataType: z.number().int(),
        value: z
          .number()
          .int("Credit duration must be a whole number")
          .min(0, "Credit duration must be at least 0 months")
          .max(480, "Credit duration must be at most 480 months"),
      })
      .optional(),
    salaryAccountBankDD: DDFieldNumber.optional(),
    leadId: z.number().int(),
    sellerIdDD: DDFieldString.optional(),
    emiratesId: z.string().nullable().optional(),
    creditHistoryDD: DDFieldNumber.optional(),
    isDetailComplete: z.number().int().optional(),
    arnNumber: z.string().optional(),
  })
  .strict();

export const getLeadDetailsRequestSchema = z
  .object({
    leadId: z.number().int(),
  })
  .strict();

/**
 * Schema for get lead details response
 */
export const getLeadDetailsResponseSchema = z.object({
  // Lead Information
  leadId: z.number().nullable(),
  utmSource: z.string().nullable().optional(),
  nationalityId: z.number().nullable().optional(),
  roleId: z.number(),
  createdOn: z.string(),
  leadStatusId: z.number(),
  assignedToName: z.string().nullable().optional(),
  income: z.number().nullable().optional(),
  cityId: z.number().nullable().optional(),
  name: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  mobileNumber: z.string().nullable().optional(),
  dob: z.date().nullable().optional(),
  gender: z.number().nullable().optional(),
  emiratesId: z.string().nullable().optional(),
  arnNumber: z.string().nullable().optional(),
  enquiryId: z.number(),
  stampId: z.number().nullable().optional(),

  // DD Fields (all optional)
  employerNameDD: DDFieldString.optional(),
  employmentTypeDD: DDFieldNumber.optional(),
  creditHistoryAgeDD: DDFieldNumber.optional(),
  creditScoreRangeDD: DDFieldNumber.optional(),
  creditCardDesignationDD: DDFieldNumber.optional(),
  websiteDD: DDFieldString.optional(),
  landLineDD: DDFieldString.optional(),
  existingCreditDurationDD: DDFieldNumber.optional(),
  salaryAccountBankDD: DDFieldNumber.optional(),
  sellerIdDD: DDFieldString.optional(),
  creditHistoryDD: DDFieldNumber.optional(),

  // Selection Data
  selectionData: z
    .object({
      planId: z.number(),
      planName: z.string(),
      insurerId: z.number(),
      insurerName: z.string(),
    })
    .nullable()
    .optional(),
});

/**
 * Schema for lead creation response based on actual API response
 */
export const leadCreateResponseSchema = z.object({
  enquiryId: z.string(),
  visitId: z.number(),
  pbMatrixLeadId: z.number(),
  next: z.object({
    type: z.string(),
    config: z.object({
      url: z.string().url(),
    }),
    isNativeScreen: z.boolean(),
  }),
  pbCustomerId: z.number(),
  leadCreatedAt: z.string().datetime(),
});

export const leadUpdateResponseSchema = z.object({
  message: z.string(),
});

export const leadCreateSuccessResponseSchema = wrapSuccessResponse(
  leadCreateResponseSchema,
);
export const leadUpdateSuccessResponseSchema = wrapSuccessResponse(
  leadUpdateResponseSchema,
);
export const getLeadDetailsSuccessResponseSchema = wrapSuccessResponse(
  getLeadDetailsResponseSchema,
);

/**
 * Type inference from the schema
 */
export type LeadCreateRequest = z.infer<typeof leadCreateRequestSchema>;
export type LeadCreateResponse = z.infer<typeof leadCreateResponseSchema>;
export type LeadUpdateRequest = z.infer<typeof leadUpdateRequestSchema>;
export type GetLeadDetailsRequest = z.infer<typeof getLeadDetailsRequestSchema>;
export type GetLeadDetailsResponse = z.infer<
  typeof getLeadDetailsResponseSchema
>;
/**
 * Zod schema for list leads request
 */
export const listLeadsRequestSchema = z
  .object({
    startDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in yyyy-mm-dd format")
      .refine(
        (dateStr) => {
          const date = new Date(dateStr);
          const today = new Date(); // Current time
          const maxDate = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000);
          return date >= maxDate && date <= today;
        },
        {
          message:
            "Start date must be within the last 90 days and not greater than today",
        },
      ),
    endDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in yyyy-mm-dd format")
      .optional(),
    roleIds: z.array(z.number().int()).optional(),
    singleUserId: z.string().optional(),
    leadIds: z.array(z.number().int()).optional(),
    page: z.number().int().positive().default(1),
    stampId: z.number().int().optional(),
    limit: z.number().int().positive().max(100).default(50),
  })
  .strict();

/**
 * Schema for list leads response
 */
export const listLeadsResponseSchema = z.object({
  leads: z.array(
    z.object({
      leadId: z.number(),
      utmSource: z.string().nullable(),
      nationality: z.number().nullable(),
      roleId: z.number(),
      createdOn: z.string(),
      userName: z.string(),
      // assignedToName: z.string(),
      leadStatusId: z.number(),
      stampId: z.number().optional(),
    }),
  ),
  pagination: z.object({
    total: z.number(),
    page: z.number(),
    limit: z.number(),
    totalPages: z.number(),
    hasNext: z.boolean(),
    hasPrev: z.boolean(),
  }),
});

export type LeadCreateSuccessResponseSchema = z.infer<
  typeof leadCreateSuccessResponseSchema
>;
export type LeadUpdateSuccessResponseSchema = z.infer<
  typeof leadUpdateSuccessResponseSchema
>;
export type ListLeadsRequest = z.infer<typeof listLeadsRequestSchema>;
export type ListLeadsResponse = z.infer<typeof listLeadsResponseSchema>;
export const listLeadsSuccessResponseSchema = wrapSuccessResponse(
  listLeadsResponseSchema,
);
export type ListLeadsSuccessResponseSchema = z.infer<
  typeof listLeadsSuccessResponseSchema
>;

/**
 * Zod schema for get quotes request
 */
export const getQuotesRequestSchema = z
  .object({
    leadId: z.number().int().positive("Lead ID must be a positive integer"),
  })
  .strict();

/**
 * Zod schema for quote limit
 */
export const quoteLimitSchema = z.object({
  id: z.string(),
  name: z.string(),
  value: z.string(),
  unit: z.string(),
});

/**
 * Zod schema for quote referral
 */
export const quoteReferralSchema = z.object({
  isReferral: z.number(),
  message: z.string(),
});

/**
 * Zod schema for quote premium amount
 */
export const quotePremiumSchema = z.object({
  id: z.number(),
  label: z.string(),
  unit: z.string(),
  value: z.string().optional(), // value can be optional in some cases
});

/**
 * Zod schema for quote payment frequency
 */
export const quotePaymentFrequencySchema = z.object({
  id: z.number(),
  unit: z.string(),
});

/**
 * Zod schema for quote selection
 */
export const quoteSelectionSchema = z.object({
  supplierId: z.number(),
  planId: z.number(),
  redirectionUrl: z.string(),
  paymentFrequencyId: z.number(),
  profileId: z.number(),
  iosRedirection: z.string(),
  androidRedirection: z.string(),
});

/**
 * Zod schema for quote offer
 */
export const quoteOfferSchema = z.object({
  planId: z.number(),
  id: z.number(),
  image: z.string(),
  description: z.string(),
});

/**
 * Zod schema for quote plan type
 */
export const quotePlanTypeSchema = z.object({
  planId: z.number(),
  id: z.number(),
  name: z.string(),
});

/**
 * Zod schema for quote reward type
 */
export const quoteRewardTypeSchema = z.object({
  planId: z.number(),
  id: z.number(),
  name: z.string(),
});

/**
 * Zod schema for quote feature
 */
export const quoteFeatureSchema = z.object({
  title: z.string(),
  checked: z.boolean(),
  strike: z.boolean(),
});

/**
 * Zod schema for individual quote
 */
export const quoteSchema = z.object({
  providerId: z.number(),
  promotionalMessage: z.string(),
  planId: z.number(),
  isDapiEligible: z.number(),
  planName: z.string(),
  providerLogo: z.string(),
  providerName: z.string(),
  badge1: z.string(),
  badge2: z.string(),
  planPriority: z.number(),
  insurerPriority: z.number(),
  limits: z.array(quoteLimitSchema),
  subLimits: z.array(z.any()),
  referral: quoteReferralSchema,
  addonPremium: quotePremiumSchema,
  basicPremium: quotePremiumSchema,
  discountedPremium: quotePremiumSchema,
  paidAmount: z.object({
    id: z.number().optional(), // paidAmount id can be undefined
    label: z.string(),
    unit: z.string(),
    value: z.string().optional(),
  }),
  tax: quotePremiumSchema,
  payableAmount: z.object({
    id: z.number(),
    label: z.string(),
    unit: z.string(),
    value: z.string().optional(), // payableAmount value is optional
  }),
  pbDiscount: quotePremiumSchema,
  insurerDiscount: quotePremiumSchema,
  insurerCharges: quotePremiumSchema,
  paymentFrequency: quotePaymentFrequencySchema,
  selection: quoteSelectionSchema,
  isPaymentEnabled: z.boolean(),
  offers: z.array(quoteOfferSchema),
  planTypes: z.array(quotePlanTypeSchema),
  rewardTypes: z.array(quoteRewardTypeSchema),
  features: z.array(quoteFeatureSchema),
  tabs: z.string(),
  checkEligibility: z.null(),
});

/**
 * Zod schema for quotes response
 */
export const quotesResponseSchema = z.object({
  title: z.string(),
  quotes: z.array(quoteSchema),
});

/**
 * Zod schema for get quotes response (just the quotes array)
 */
export const getQuotesResponseSchema = z.array(quoteSchema);

export type GetQuotesRequest = z.infer<typeof getQuotesRequestSchema>;
export type GetQuotesResponse = z.infer<typeof getQuotesResponseSchema>;
export const getQuotesSuccessResponseSchema = wrapSuccessResponse(
  getQuotesResponseSchema,
);
export type GetQuotesSuccessResponseSchema = z.infer<
  typeof getQuotesSuccessResponseSchema
>;

/**
 * Zod schema for save user selection request
 */
export const saveUserSelectionRequestSchema = z
  .object({
    leadId: z.number().int().positive("Lead ID must be a positive integer"),
    selection: z.object({
      supplierId: z
        .number()
        .int()
        .positive("Supplier ID must be a positive integer"),
      planId: z.number().int().positive("Plan ID must be a positive integer"),
      redirectionUrl: z
        .url("Redirection URL must be a valid URL")
        .optional()
        .or(z.literal("")),
      paymentFrequencyId: z
        .number()
        .int()
        .positive("Payment frequency ID must be a positive integer"),
      profileId: z
        .number()
        .int()
        .min(0, "Profile ID must be a non-negative integer"),
      iosRedirection: z
        .url("iOS redirection URL must be a valid URL")
        .optional()
        .or(z.literal("")),
      androidRedirection: z
        .url("Android redirection URL must be a valid URL")
        .optional()
        .or(z.literal("")),
    }),
  })
  .strict();

/**
 * Zod schema for next config
 */
export const nextConfigSchema = z.object({
  bankUrl: z
    .string()
    .url("Bank URL must be a valid URL")
    .optional()
    .or(z.literal("")),
  url: z.string().url("URL must be a valid URL").optional().or(z.literal("")),
});

/**
 * Zod schema for next object
 */
export const nextSchema = z.object({
  type: z.string(),
  config: nextConfigSchema,
});

/**
 * Zod schema for save user selection response
 */
export const saveUserSelectionResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  next: nextSchema,
});

export type SaveUserSelectionRequest = z.infer<
  typeof saveUserSelectionRequestSchema
>;
export type SaveUserSelectionResponse = z.infer<
  typeof saveUserSelectionResponseSchema
>;
export const saveUserSelectionSuccessResponseSchema = wrapSuccessResponse(
  saveUserSelectionResponseSchema,
);
export type SaveUserSelectionSuccessResponseSchema = z.infer<
  typeof saveUserSelectionSuccessResponseSchema
>;

/**
 * Zod schema for get lead count by stamp ID request
 */
export const getLeadCountByStampIdRequestSchema = listLeadsRequestSchema;

/**
 * Zod schema for lead count by stamp ID item
 */
export const leadCountByStampIdItemSchema = z.object({
  stampId: z.number().int(),
  name: z.string(),
  count: z.number().int().min(0),
});

/**
 * Zod schema for get lead count by stamp ID response
 */
export const getLeadCountByStampIdResponseSchema = z.array(
  leadCountByStampIdItemSchema,
);

export type GetLeadCountByStampIdRequest = z.infer<
  typeof getLeadCountByStampIdRequestSchema
>;
export type LeadCountByStampIdItem = z.infer<
  typeof leadCountByStampIdItemSchema
>;
export type GetLeadCountByStampIdResponse = z.infer<
  typeof getLeadCountByStampIdResponseSchema
>;

export const getLeadCountByStampIdSuccessResponseSchema = wrapSuccessResponse(
  getLeadCountByStampIdResponseSchema,
);
export type GetLeadCountByStampIdSuccessResponseSchema = z.infer<
  typeof getLeadCountByStampIdSuccessResponseSchema
>;

/**
 * Zod schema for field in view details response
 */
export const viewDetailsFieldSchema = z.object({
  Name: z.string(),
  Value: z.union([z.string(), z.number(), z.boolean()]),
});

/**
 * Zod schema for row in view details response
 */
export const viewDetailsRowSchema = z.object({
  Fields: z.array(viewDetailsFieldSchema),
});

/**
 * Zod schema for section in view details response
 */
export const viewDetailsSectionSchema = z
  .object({
    Header: z.string(),
    Rows: z.array(viewDetailsRowSchema),
  })
  .passthrough(); // Allow additional properties

/**
 * Zod schema for view lead details response
 */
export const viewLeadDetailsResponseSchema = z.array(viewDetailsSectionSchema);

export type ViewDetailsField = z.infer<typeof viewDetailsFieldSchema>;
export type ViewDetailsRow = z.infer<typeof viewDetailsRowSchema>;
export type ViewDetailsSection = z.infer<typeof viewDetailsSectionSchema>;
export type ViewLeadDetailsResponse = z.infer<
  typeof viewLeadDetailsResponseSchema
>;

export const viewLeadDetailsSuccessResponseSchema = wrapSuccessResponse(
  viewLeadDetailsResponseSchema,
);
export type ViewLeadDetailsSuccessResponseSchema = z.infer<
  typeof viewLeadDetailsSuccessResponseSchema
>;
